import {
  QueryObserverResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../store/global-store/global-store';
import type { InitiativeTasksConfig as InitiativeTasksFilters } from '../initiative-client/initiative-client.type';
import type { MinifiedTask } from '../tasks-client/task-client.type';
import {
  bookmarkGoal,
  bookmarkOutcome,
  deleteGoal,
  deleteOutcome,
  getGoal,
  getGoalProgressChartData,
  getGoals,
  getGoalsMinimal,
  getGoalTasks,
  getGoalTeamFocusChartData,
  getGoalWorkChartData,
  getMindMap,
  getOutcome,
  getOutcomeProgressChartData,
  getOutcomes,
  getOutcomesMinimal,
  getOutcomeTasks,
  getOutcomeTeamFocusChartData,
  getOutcomeWorkChartData,
  getRoadmap,
  hideOutcome,
  unbookmarkGoal,
  unbookmarkOutcome,
  unhideOutcome,
  updateGoal,
  updateOutcome,
} from './strategy-client';
import type {
  Goal,
  GoalTasksFilters,
  HideOutcomeParams,
  MindMapData,
  MinimalGoal,
  MinimalOutcome,
  Outcome,
  PreSaveGoal,
  PreSaveOutcome,
  RoadmapData,
  ToggleVisibilityResponse,
  UnhideOutcomeParams,
} from './strategy-client.type';

/**
 * Hook to fetch all outcomes for the current org+portfolio.
 *
 * `includeHidden` is opt-in and defaults to `false` so that the broad set of
 * consumers (Outcomes table, Initiative List, Risk Analysis, CSV export, the
 * Strategy Entity Modal's outcome picker, etc.) keep returning visible-only
 * outcomes regardless of any UI toggle. The Strategy Overview page is the only
 * caller that opts in by passing the persisted "Show Hidden" toggle from
 * `useStrategyStore`. The flag is part of the queryKey so cache entries for
 * visible-only vs. include-hidden remain separated.
 *
 * @param {Omit<UseQueryOptions<Outcome[]>, 'queryKey' | 'queryFn'>} options - Optional query options for configuring the query behavior
 * @param {boolean} includeHidden - When true, requests outcomes hidden in the active portfolio in addition to visible ones; each item carries `is_hidden` for caller-side rendering
 * @returns Object containing outcomes array and query state
 */
function useOutcomes(
  options?: Omit<UseQueryOptions<Outcome[]>, 'queryKey' | 'queryFn'>,
  includeHidden = false,
): {
  outcomes: Outcome[];
  query: QueryObserverResult<Outcome[]>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const query = useQuery<Outcome[]>({
    queryKey: ['outcomes', organizationId, portfolioId, includeHidden],
    queryFn: () => getOutcomes(organizationId ?? '', portfolioId, includeHidden),
    ...options,
    enabled: !!organizationId && options?.enabled !== false,
  });

  return {
    outcomes: query.data || [],
    query,
  };
}

/**
 * Hook to fetch minimal outcomes (id and name only)
 *
 * @param {Omit<UseQueryOptions<MinimalOutcome[]>, 'queryKey' | 'queryFn'>} options - Optional query options for configuring the query behavior
 * @returns Object containing minimal outcomes array and query state
 */
function useOutcomesMinimal(options?: Omit<UseQueryOptions<MinimalOutcome[]>, 'queryKey' | 'queryFn'>): {
  outcomes: MinimalOutcome[];
  query: QueryObserverResult<MinimalOutcome[]>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const query = useQuery<MinimalOutcome[]>({
    queryKey: ['outcomes-minimal', organizationId, portfolioId],
    queryFn: () => getOutcomesMinimal(organizationId ?? '', portfolioId),
    ...options,
    enabled: !!organizationId && options?.enabled !== false,
  });

  return {
    outcomes: query.data || [],
    query,
  };
}

/**
 * Hook to fetch a single outcome
 *
 * @param {string} outcomeId - The ID of the outcome to fetch
 * @param {UseQueryOptions<Outcome>} options - Optional query options for configuring the query behavior
 * @returns {{ outcome: Outcome | undefined; query: QueryObserverResult<Outcome> }} Object containing the outcome and query state
 */
function useOutcome(
  outcomeId: string | undefined,
  options?: UseQueryOptions<Outcome>,
): {
  outcome: Outcome | undefined;
  query: QueryObserverResult<Outcome>;
} {
  const query = useQuery<Outcome>({
    queryKey: ['outcome', outcomeId],
    queryFn: () => getOutcome(outcomeId!),
    enabled: !!outcomeId,
    ...options,
  });

  return {
    outcome: query.data,
    query,
  };
}

/**
 * Hook to update an outcome
 *
 * @param {UseMutationOptions<Outcome, unknown, { outcomeId: string; data: PreSaveOutcome }>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for updating an outcome
 */
function useUpdateOutcomeMutation(
  options?: UseMutationOptions<Outcome, unknown, { outcomeId: string; data: PreSaveOutcome }>,
): UseMutationResult<Outcome, unknown, { outcomeId: string; data: PreSaveOutcome }> {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);

  return useMutation<Outcome, unknown, { outcomeId: string; data: PreSaveOutcome }>({
    mutationFn: ({ outcomeId, data }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to update an outcome');
      }
      return updateOutcome(outcomeId, {
        ...data,
        organization_id: organizationId,
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({
        queryKey: ['outcome', variables.outcomeId],
      });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to delete an outcome
 *
 * @param {UseMutationOptions<void, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for deleting an outcome
 */
function useDeleteOutcomeMutation(
  options?: UseMutationOptions<void, unknown, string>,
): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: deleteOutcome,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to fetch all goals
 *
 * @param {UseQueryOptions<Goal[]>} options - Optional query options for configuring the query behavior
 * @returns Object containing goals array and query state
 */
function useGoals(options?: UseQueryOptions<Goal[]>): {
  goals: Goal[];
  query: QueryObserverResult<Goal[]>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const query = useQuery<Goal[]>({
    queryKey: ['goals', organizationId, portfolioId],
    queryFn: () => getGoals(organizationId ?? '', portfolioId),
    enabled: !!organizationId,
    ...options,
  });

  return {
    goals: query.data || [],
    query,
  };
}

/**
 * Hook to fetch minimal goals (id and name only)
 *
 * @param {UseQueryOptions<MinimalGoal[]>} options - Optional query options for configuring the query behavior
 * @returns Object containing minimal goals array and query state
 */
function useGoalsMinimal(options?: UseQueryOptions<MinimalGoal[]>): {
  goals: MinimalGoal[];
  query: QueryObserverResult<MinimalGoal[]>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const query = useQuery<MinimalGoal[]>({
    queryKey: ['goals-minimal', organizationId, portfolioId],
    queryFn: () => getGoalsMinimal(organizationId ?? '', portfolioId),
    enabled: !!organizationId,
    ...options,
  });

  return {
    goals: query.data || [],
    query,
  };
}

/**
 * Hook to fetch a single goal
 *
 * @param {string} goalId - The ID of the goal to fetch
 * @param {UseQueryOptions<Goal>} options - Optional query options for configuring the query behavior
 * @returns Object containing the goal and query state
 */
function useGoal(
  goalId: string | undefined,
  options?: UseQueryOptions<Goal>,
): {
  goal: Goal | undefined;
  query: QueryObserverResult<Goal>;
} {
  const query = useQuery<Goal>({
    queryKey: ['goal', goalId],
    queryFn: () => getGoal(goalId!),
    enabled: !!goalId,
    ...options,
  });

  return {
    goal: query.data,
    query,
  };
}

/**
 * Hook to update a goal
 *
 * @param {UseMutationOptions<Goal, unknown, { goalId: string; data: PreSaveGoal }>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for updating a goal
 */
function useUpdateGoalMutation(
  options?: UseMutationOptions<Goal, unknown, { goalId: string; data: PreSaveGoal }>,
): UseMutationResult<Goal, unknown, { goalId: string; data: PreSaveGoal }> {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);

  return useMutation<Goal, unknown, { goalId: string; data: PreSaveGoal }>({
    mutationFn: ({ goalId, data }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to update a goal');
      }
      return updateGoal(goalId, {
        ...data,
        organization_id: organizationId,
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal', variables.goalId] });
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to delete a goal
 *
 * @param {UseMutationOptions<void, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for deleting a goal
 */
function useDeleteGoalMutation(
  options?: UseMutationOptions<void, unknown, string>,
): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: deleteGoal,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to fetch roadmap data for a portfolio
 *
 * @param {string} portfolioId - The ID of the portfolio to fetch roadmap data for
 * @param {UseQueryOptions<RoadmapData>} options - Optional query options for configuring the query behavior
 * @returns Object containing roadmap data and query state
 */
function useRoadmap(
  portfolioId: string | undefined,
  options?: UseQueryOptions<RoadmapData>,
): {
  roadmap: RoadmapData | undefined;
  query: QueryObserverResult<RoadmapData>;
} {
  const i18n = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery<RoadmapData>({
    queryKey: ['roadmap', portfolioId, organizationId],
    queryFn: () => getRoadmap(portfolioId!, organizationId!),
    enabled: !!portfolioId && !!organizationId,
    ...options,
  });

  // Transform "No Goal" and "No Outcome" to i18n translations
  const transformedRoadmap = query.data
    ? {
        ...query.data,
        goals: query.data.goals.map((goal) => ({
          ...goal,
          name: goal.name === 'No Goal' ? i18n.t('strategy.roadmap.no_goal') : goal.name,
          outcomes: goal.outcomes.map((outcome) => ({
            ...outcome,
            name: outcome.name === 'No Objective' ? i18n.t('strategy.roadmap.no_outcome') : outcome.name,
          })),
        })),
      }
    : undefined;

  return {
    roadmap: transformedRoadmap,
    query,
  };
}

/**
 * Hook to fetch mind map data for a portfolio
 *
 * @param {string} portfolioId - The ID of the portfolio to fetch mind map data for
 * @param {UseQueryOptions<MindMapData>} options - Optional query options for configuring the query behavior
 * @returns Object containing mind map graph data and query state
 */
function useMindMap(
  portfolioId: string | undefined,
  options?: UseQueryOptions<MindMapData>,
): {
  graph: MindMapData | undefined;
  query: QueryObserverResult<MindMapData>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery<MindMapData>({
    queryKey: ['mind-map', portfolioId, organizationId],
    queryFn: () => getMindMap(portfolioId!, organizationId!),
    enabled: !!portfolioId && !!organizationId,
    ...options,
  });

  return {
    graph: query.data,
    query,
  };
}

/**
 * Hook to fetch tasks for an outcome
 *
 * @param {string} outcomeId - The ID of the outcome to fetch tasks for
 * @param {InitiativeTasksFilters} filters - Filters to apply to the task query
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing tasks data and query state
 */
function useOutcomeTasks(
  outcomeId: string | undefined,
  filters: InitiativeTasksFilters,
  options?: Partial<UseQueryOptions<MinifiedTask[]>>,
): {
  data: MinifiedTask[] | undefined;
  query: QueryObserverResult<MinifiedTask[]>;
} {
  const query = useQuery<MinifiedTask[]>({
    queryKey: [
      'outcome-tasks',
      outcomeId,
      filters.filter,
      filters.date,
      filters.epicId,
      filters.projectId,
      filters.initiativeId,
    ],
    queryFn: () => getOutcomeTasks(outcomeId!, filters),
    enabled: !!outcomeId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch tasks for a goal
 *
 * @param {string} goalId - The ID of the goal to fetch tasks for
 * @param {GoalTasksFilters} filters - Filters to apply to the task query
 * @param {UseQueryOptions<MinifiedTask[]>} options - Optional query options for configuring the query behavior
 * @returns Object containing tasks data and query state
 */
function useGoalTasks(
  goalId: string | undefined,
  filters: GoalTasksFilters,
  options?: Partial<UseQueryOptions<MinifiedTask[]>>,
): {
  data: MinifiedTask[] | undefined;
  query: QueryObserverResult<MinifiedTask[]>;
} {
  const query = useQuery<MinifiedTask[]>({
    queryKey: [
      'goal-tasks',
      goalId,
      filters.filter,
      filters.date,
      filters.epicId,
      filters.projectId,
      filters.outcomeId,
      filters.initiativeId,
    ],
    queryFn: () => getGoalTasks(goalId!, filters),
    enabled: !!goalId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated progress chart data for an outcome
 *
 * @param {string} outcomeId - The ID of the outcome to fetch progress chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing progress chart data and query state
 */
function useOutcomeProgressChartData(
  outcomeId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['outcome-progress-chart-data', outcomeId, topN],
    queryFn: () => getOutcomeProgressChartData(outcomeId!, topN),
    enabled: !!outcomeId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated team focus chart data for an outcome
 *
 * @param {string} outcomeId - The ID of the outcome to fetch team focus chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing team focus chart data and query state
 */
function useOutcomeTeamFocusChartData(
  outcomeId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['outcome-team-focus-chart-data', outcomeId, topN],
    queryFn: () => getOutcomeTeamFocusChartData(outcomeId!, topN),
    enabled: !!outcomeId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated work chart data for an outcome
 *
 * @param {string} outcomeId - The ID of the outcome to fetch work chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing work chart data and query state
 */
function useOutcomeWorkChartData(
  outcomeId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['outcome-work-chart-data', outcomeId, topN],
    queryFn: () => getOutcomeWorkChartData(outcomeId!, topN),
    enabled: !!outcomeId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated progress chart data for a goal
 *
 * @param {string} goalId - The ID of the goal to fetch progress chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing progress chart data and query state
 */
function useGoalProgressChartData(
  goalId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['goal-progress-chart-data', goalId, topN],
    queryFn: () => getGoalProgressChartData(goalId!, topN),
    enabled: !!goalId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated team focus chart data for a goal
 *
 * @param {string} goalId - The ID of the goal to fetch team focus chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing team focus chart data and query state
 */
function useGoalTeamFocusChartData(
  goalId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['goal-team-focus-chart-data', goalId, topN],
    queryFn: () => getGoalTeamFocusChartData(goalId!, topN),
    enabled: !!goalId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to fetch aggregated work chart data for a goal
 *
 * @param {string} goalId - The ID of the goal to fetch work chart data for
 * @param {number} topN - Number of top initiatives to show (default: 6)
 * @param {UseQueryOptions<any>} options - Optional query options for configuring the query behavior
 * @returns Object containing work chart data and query state
 */
function useGoalWorkChartData(
  goalId: string | undefined,
  topN = 6,
  options?: UseQueryOptions<any>,
): {
  data: any | undefined;
  query: QueryObserverResult<any>;
} {
  const query = useQuery<any>({
    queryKey: ['goal-work-chart-data', goalId, topN],
    queryFn: () => getGoalWorkChartData(goalId!, topN),
    enabled: !!goalId,
    ...options,
  });

  return {
    data: query.data,
    query,
  };
}

/**
 * Hook to bookmark a goal
 *
 * @param {UseMutationOptions<Goal, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for bookmarking a goal
 */
function useBookmarkGoalMutation(
  options?: UseMutationOptions<Goal, unknown, string>,
): UseMutationResult<Goal, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Goal, unknown, string>({
    mutationFn: bookmarkGoal,
    onSuccess: (data, goalId, context) => {
      // Invalidate queries to trigger refetch with complete data
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal', goalId] });
      (options?.onSuccess as any)?.(data, goalId, context);
    },
    ...options,
  });
}

/**
 * Hook to unbookmark a goal
 *
 * @param {UseMutationOptions<Goal, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for unbookmarking a goal
 */
function useUnbookmarkGoalMutation(
  options?: UseMutationOptions<Goal, unknown, string>,
): UseMutationResult<Goal, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Goal, unknown, string>({
    mutationFn: unbookmarkGoal,
    onSuccess: (data, goalId, context) => {
      // Invalidate queries to trigger refetch with complete data
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal', goalId] });
      (options?.onSuccess as any)?.(data, goalId, context);
    },
    ...options,
  });
}

/**
 * Hook to bookmark an outcome
 *
 * @param {UseMutationOptions<Outcome, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for bookmarking an outcome
 */
function useBookmarkOutcomeMutation(
  options?: UseMutationOptions<Outcome, unknown, string>,
): UseMutationResult<Outcome, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Outcome, unknown, string>({
    mutationFn: bookmarkOutcome,
    onSuccess: (data, outcomeId, context) => {
      // Invalidate queries to trigger refetch with complete data
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['outcome', outcomeId] });
      (options?.onSuccess as any)?.(data, outcomeId, context);
    },
    ...options,
  });
}

/**
 * Hook to unbookmark an outcome
 *
 * @param {UseMutationOptions<Outcome, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for unbookmarking an outcome
 */
function useUnbookmarkOutcomeMutation(
  options?: UseMutationOptions<Outcome, unknown, string>,
): UseMutationResult<Outcome, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Outcome, unknown, string>({
    mutationFn: unbookmarkOutcome,
    onSuccess: (data, outcomeId, context) => {
      // Invalidate queries to trigger refetch with complete data
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['outcome', outcomeId] });
      (options?.onSuccess as any)?.(data, outcomeId, context);
    },
    ...options,
  });
}

/**
 * Hook to hide an outcome in a specific portfolio
 *
 * @param {UseMutationOptions<ToggleVisibilityResponse, unknown, HideOutcomeParams>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for hiding an outcome
 */
function useHideOutcomeMutation(
  options?: UseMutationOptions<ToggleVisibilityResponse, unknown, HideOutcomeParams>,
): UseMutationResult<ToggleVisibilityResponse, unknown, HideOutcomeParams> {
  const queryClient = useQueryClient();

  return useMutation<ToggleVisibilityResponse, unknown, HideOutcomeParams>({
    mutationFn: ({ outcomeId, portfolioId }) => hideOutcome(outcomeId, portfolioId),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['outcome', variables.outcomeId] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * Hook to unhide an outcome in a specific portfolio
 *
 * @param {UseMutationOptions<ToggleVisibilityResponse, unknown, UnhideOutcomeParams>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for unhiding an outcome
 */
function useUnhideOutcomeMutation(
  options?: UseMutationOptions<ToggleVisibilityResponse, unknown, UnhideOutcomeParams>,
): UseMutationResult<ToggleVisibilityResponse, unknown, UnhideOutcomeParams> {
  const queryClient = useQueryClient();

  return useMutation<ToggleVisibilityResponse, unknown, UnhideOutcomeParams>({
    mutationFn: ({ outcomeId, portfolioId }) => unhideOutcome(outcomeId, portfolioId),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['outcomes'] });
      queryClient.invalidateQueries({ queryKey: ['outcome', variables.outcomeId] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export {
  useBookmarkGoalMutation,
  useBookmarkOutcomeMutation,
  useDeleteGoalMutation,
  useDeleteOutcomeMutation,
  useGoal,
  useGoalProgressChartData,
  useGoals,
  useGoalsMinimal,
  useGoalTasks,
  useGoalTeamFocusChartData,
  useGoalWorkChartData,
  useHideOutcomeMutation,
  useMindMap,
  useOutcome,
  useOutcomeProgressChartData,
  useOutcomes,
  useOutcomesMinimal,
  useOutcomeTasks,
  useOutcomeTeamFocusChartData,
  useOutcomeWorkChartData,
  useRoadmap,
  useUnbookmarkGoalMutation,
  useUnbookmarkOutcomeMutation,
  useUnhideOutcomeMutation,
  useUpdateGoalMutation,
  useUpdateOutcomeMutation,
};
